import * as React from 'react'
import { VendorProfileLink } from '@/components/VendorProfileLink'
import { Mail, X } from 'lucide-react'
import { useInviteEventVendorContact, useRemoveEventVendor } from '@/hooks/useEvents'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { FormError } from '@/components/ui/form-error'
import { UserStatusBadge, inviteActionLabel } from '@/components/UserStatusBadge'
import type { EventDetailDto, EventPlannerDto } from '@lib/dto/event.dto'

/** A company assigned to the event, however it was assigned: what a row needs of it. */
type Assignment = Pick<EventPlannerDto, 'assignmentId' | 'vendorId' | 'companyName' | 'contact'>

/**
 * One company assigned to the event, its contact, and the actions on them: the
 * planner's slot and each other vendor's row read the same way, and invite and
 * take off the same way. Without Edit, the company and its contact only.
 */
export function EventAssignmentRow({
  event,
  assignment,
  icon,
  category,
  removeDescription,
  canEdit,
}: {
  event: EventDetailDto
  assignment: Assignment
  icon: React.ReactNode
  /** What it is on the event for, when the section does not already say. */
  category?: string
  /** What taking it off does, for the confirmation. */
  removeDescription: string
  /** Whether the viewer can edit events, and so invite the contact or take the company off. */
  canEdit: boolean
}) {
  const invite = useInviteEventVendorContact(event.id)
  const remove = useRemoveEventVendor(event.id)
  const [confirming, setConfirming] = React.useState(false)
  const [msg, setMsg] = React.useState<string | null>(null)
  const contact = assignment.contact

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <span className="shrink-0 text-ink-muted [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          <VendorProfileLink vendorId={assignment.vendorId} name={assignment.companyName} />
          {category === undefined ? null : <span className="font-normal text-ink-muted"> · {category}</span>}
        </p>
        {/* Who is written to. With nobody named, the company's owners are. */}
        <p className="truncate text-xs text-ink-muted">
          {contact ? `${contact.name} · ${contact.email}` : 'No contact — the company’s owners are written to.'}
        </p>
        {msg ? <FormError size="sm">{msg}</FormError> : null}
      </div>
      {contact ? (
        <UserStatusBadge
          status={contact.status}
          invitedAt={contact.invitedAt}
          inviteExpiresAt={contact.inviteExpiresAt}
          inviteKeptOpen={contact.inviteKeptOpen}
        />
      ) : null}
      {canEdit ? (
        <>
          <div className="flex items-center gap-1.5">
            {contact?.status === 'PENDING' ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={invite.isPending}
                onClick={() =>
                  invite.mutate(
                    { assignmentId: assignment.assignmentId },
                    { onSuccess: () => setMsg(null), onError: (e) => setMsg(requestErrorMessage(e)) },
                  )
                }
              >
                <Mail className="h-4 w-4" /> {inviteActionLabel(contact.invitedAt)}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Take ${assignment.companyName} off this event`}
              onClick={() => {
                remove.reset()
                setConfirming(true)
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <ConfirmDialog
            open={confirming}
            onOpenChange={setConfirming}
            title={`Take ${assignment.companyName} off this event?`}
            description={removeDescription}
            confirmLabel="Take off event"
            destructive
            busy={remove.isPending}
            error={remove.error ? requestErrorMessage(remove.error) : null}
            onConfirm={() => remove.mutate({ assignmentId: assignment.assignmentId }, { onSuccess: () => setConfirming(false) })}
          />
        </>
      ) : null}
    </li>
  )
}
